import { useCallback } from "react"

import type { NewRequestFormValues } from "../types"
import { toSalesRequestCreateInput } from "../utils/toSalesRequestCreateInput"

import type { CreateSalesRequestResult } from "./useCreateSalesRequest"
import { useCreateSalesRequest } from "./useCreateSalesRequest"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SubmitQuoteRequestParams {
  formValues: NewRequestFormValues
  emailReceiverContactIds: string[]
  emailNote: string
  getSuccessMessage: (requestIds: string[]) => string
  // skip success toast — this call only creates a background DRAFT (e.g. "Continue to Email")
  silent?: boolean
}

export interface UseSubmitQuoteRequestReturn {
  submitQuoteRequest: (params: SubmitQuoteRequestParams) => Promise<CreateSalesRequestResult>
  isSubmitting: boolean
}

export const useSubmitQuoteRequest = (): UseSubmitQuoteRequestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createSalesRequest, isCreating } = useCreateSalesRequest()

  const submitQuoteRequest = useCallback(
    async ({
      formValues,
      emailReceiverContactIds,
      emailNote,
      getSuccessMessage,
      silent,
    }: SubmitQuoteRequestParams): Promise<CreateSalesRequestResult> => {
      const pricingByProductId = formValues.pricingByProductId
      if (!pricingByProductId) {
        notify({ type: NotificationType.Error, message: "Pricing data is missing. Please go back and try again." })
        return { success: false }
      }
      const input = toSalesRequestCreateInput(formValues, pricingByProductId, { emailReceiverContactIds, emailNote })
      return createSalesRequest(input, { getSuccessMessage, silent })
    },
    [createSalesRequest, notify]
  )

  return { submitQuoteRequest, isSubmitting: isCreating }
}
